import { PrivatePresentationCTA } from "@/components/PrivatePresentationCTA";
import { PRODUCTS } from "@/data/products";
import { useLang } from "@/i18n/LanguageContext";

const Univers = () => {
  const { t } = useLang();
  const u = t.univers;

  return (
    <div data-testid="univers-page">
      {/* INTRO ----------------------------------------------------------- */}
      <section className="pt-16 md:pt-28 pb-20 md:pb-28 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">{u.eyebrow}</p>
          <h1 className="display-hero text-[hsl(var(--md-ink))] max-w-[16ch] leading-[1.02]">
            {u.title}
          </h1>

          <p
            data-testid="univers-doctrine"
            className="font-display italic text-[1.35rem] md:text-[1.55rem] text-[hsl(var(--md-ink))] leading-snug mt-10 max-w-[34ch]"
          >
            {u.doctrine}
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10">
            <p className="md:col-span-7 editorial text-[hsl(var(--md-ink))] max-w-[60ch]">
              {u.intro}
            </p>
            <p className="md:col-span-4 md:col-start-9 editorial-muted">
              {u.introAsideL1}<br />
              {u.introAsideL2}<br />
              {u.introAsideL3}
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS -------------------------------------------------------- */}
      <section className="bg-[hsl(var(--md-bone))] border-y border-[hsl(var(--md-line))] py-24 md:py-32 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">{u.markersEyebrow}</p>
              <h2 className="display-1 mt-6 text-[hsl(var(--md-ink))]">
                {u.markersTitle}
              </h2>
            </div>

            <div className="md:col-span-7 md:col-start-6 grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-10">
              {u.markers.map((p) => (
                <div key={p.num}>
                  <p className="font-eyebrow text-[hsl(var(--md-stone))]">{p.num}</p>
                  <h3 className="font-display text-[1.5rem] mt-3 text-[hsl(var(--md-ink))]">
                    {p.title}
                  </h3>
                  <p className="editorial-muted mt-3" dangerouslySetInnerHTML={{ __html: p.body }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE STRIP --------------------------------------------------- */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            <div className="col-span-12 md:col-span-7 relative aspect-[5/4] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))]">
              <img
                src={PRODUCTS[3].image}
                alt={u.imageAlts.sankara}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="col-span-12 md:col-span-5 grid grid-rows-2 gap-4 md:gap-6">
              <div className="relative bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))]">
                <img
                  src={PRODUCTS[0].image}
                  alt={u.imageAlts.rosa}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="relative bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))]">
                <img
                  src={PRODUCTS[4].image}
                  alt={u.imageAlts.quincy}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLLABORATIONS ------------------------------------------------- */}
      <section className="bg-[hsl(var(--md-sand))]/40 border-y border-[hsl(var(--md-line))] py-28 md:py-40 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">{u.collabEyebrow}</p>
              <h2 className="display-1 mt-6 text-[hsl(var(--md-ink))]">
                {u.collabTitle}
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="editorial text-[hsl(var(--md-ink))]">
                {u.collabBody1}
              </p>
              <p className="editorial mt-6 text-[hsl(var(--md-ink))]">
                {u.collabBody2}
              </p>

              <div className="hairline mt-12" />

              <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-6">
                {u.locations.map(([place, line]) => (
                  <div key={place}>
                    <p className="font-display text-[1.3rem] text-[hsl(var(--md-ink))]">{place}</p>
                    <p className="font-eyebrow text-[hsl(var(--md-stone))] mt-2">{line}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ESPACES DE COLLABORATION ---------------------------------------- */}
      <section
        data-testid="univers-collab-spaces"
        className="py-24 md:py-32 relative overflow-hidden"
      >
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">{u.collabSpacesEyebrow}</p>
              <h2 className="display-1 mt-6 text-[hsl(var(--md-ink))] max-w-[16ch]">
                {u.collabSpacesTitle}
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="editorial text-[hsl(var(--md-ink))] max-w-[62ch]">
                {u.collabSpacesBody}
              </p>

              <div className="hairline mt-14" />

              <p className="font-eyebrow text-[hsl(var(--md-stone))] mt-10">{u.stagingNoteEyebrow}</p>
              <p
                data-testid="univers-staging-note"
                className="font-display italic text-[1.05rem] md:text-[1.15rem] text-[hsl(28_10%_22%)] leading-relaxed mt-4 max-w-[60ch]"
              >
                {u.stagingNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      <PrivatePresentationCTA />
    </div>
  );
};

export default Univers;
